import { createShapeId, toRichText } from 'tldraw';
import { getActivityId } from './autoLayout';
import { getActivityTitle } from './boardSelectionContext';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import {
  findDecisionTextAnchor,
  formatDecisionNoteText,
} from './decisionNoteText';
import { markShapeNewlyAdded } from './newlyAddedShapes';
import { flushBoardDocumentOps } from './persistBoardDocumentOps';
import {
  collectOccupiedPinRects,
  resolveSuggestPinFallbackPosition,
} from './suggestPinPlacement';

export { findDecisionTextAnchor, formatDecisionNoteText } from './decisionNoteText';

const NOTE_W = 280;
const NOTE_H = 88;

function collectPinAnchors(editor, activities = []) {
  if (!editor?.getCurrentPageShapes) return [];
  const titleById = new Map();
  for (const activity of Array.isArray(activities) ? activities : []) {
    const id = getActivityId(activity);
    if (!id) continue;
    titleById.set(String(id), getActivityTitle(activity));
  }
  const pins = [];
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'pin-card') continue;
    const activityId = String(shape.meta?.activityId || '').trim();
    const title = (activityId && titleById.get(activityId))
      || String(shape.meta?.title || '').trim();
    pins.push({
      title,
      x: shape.x,
      y: shape.y,
      w: shape.props?.w ?? 300,
      h: shape.props?.h ?? 400,
    });
  }
  return pins;
}

function selectedShapeBounds(editor) {
  const ids = editor?.getSelectedShapeIds?.() || [];
  if (!ids.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of ids) {
    const bounds = editor.getShapePageBounds?.(id);
    if (!bounds) continue;
    minX = Math.min(minX, bounds.x);
    minY = Math.min(minY, bounds.y);
    maxX = Math.max(maxX, bounds.x + bounds.w);
    maxY = Math.max(maxY, bounds.y + bounds.h);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function collectOccupiedRects(editor) {
  const rects = collectOccupiedPinRects(editor);
  if (!editor?.getCurrentPageShapes) return rects;
  for (const shape of editor.getCurrentPageShapes()) {
    if (
      shape.type === 'pin-card'
      || shape.type === 'suggest-pin'
      || shape.type === 'conversation-pin'
    ) {
      continue;
    }
    const w = Number(shape.props?.w) || 160;
    const h = Number(shape.props?.h) || 80;
    rects.push({ x: shape.x, y: shape.y, w, h });
  }
  return rects;
}

/**
 * Drop a visible decision note on the canvas near matching pins / selection,
 * with the same newly-added green highlight as other new objects.
 */
export function placeDecisionTextOnBoard(editor, {
  question = '',
  answer = '',
  activities = [],
  saveSnapshot,
} = {}) {
  if (!editor || editor.getInstanceState?.()?.isReadonly) return null;
  const content = formatDecisionNoteText({ question, answer });
  if (!content) return null;

  const anchor = findDecisionTextAnchor({
    answer,
    pinShapes: collectPinAnchors(editor, activities),
    selectedBounds: selectedShapeBounds(editor),
    viewport: editor.getViewportPageBounds?.() || null,
  });
  const position = resolveSuggestPinFallbackPosition(
    { x: anchor.x, y: anchor.y },
    0,
    {
      occupiedRects: collectOccupiedRects(editor),
      viewportBounds: editor.getViewportPageBounds?.() || null,
      pinSize: { w: NOTE_W, h: NOTE_H },
    }
  );

  const shapeId = createShapeId(
    `decision-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
  const claim = String(question || '').trim()
    ? `${String(question).trim()}: ${String(answer).trim()}`
    : String(answer).trim();

  const before = extractBoardDocumentSnapshot(editor);

  editor.markHistoryStoppingPoint?.('before-place-decision');
  editor.createShape({
    id: shapeId,
    type: 'text',
    x: position.x,
    y: position.y,
    props: {
      color: 'black',
      size: 's',
      w: NOTE_W,
      font: 'sans',
      textAlign: 'start',
      autoSize: false,
      scale: 1,
      richText: toRichText(content),
    },
    meta: {
      userDecision: true,
      intentSummary: claim.slice(0, 160),
    },
  });

  markShapeNewlyAdded(shapeId, editor.getContainer?.());
  try {
    editor.select(shapeId);
    editor.zoomToSelection?.({ animation: { duration: 220 } });
  } catch {
    // ignore camera helpers
  }

  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    // ignore persist errors
  }

  return shapeId;
}
